import { memo } from 'react';
import styled from 'styled-components';
import type { TransferJobType } from '@frameio/graphql-client/generated';
import { plural, select } from '@frameio/i18n';
import { CrustcrumbsView } from '@frameio/shared/components/Crustcrumbs';
import { TransferBatchStatus } from '@frameio/shared/transfers/states';
import { getMimeTypeFromFileExtension } from '@frameio/utils';
import {
  AnimatedHeight,
  Box,
  Chip,
  LinearProgress,
  ListItemContent,
  ListItemContextMenu,
  ListItemLabel,
  Text,
  theme,
} from '@frameio/vapor';
import { IconWarningFill } from '@frameio/vapor-icons';
import type { TransferBatch } from '../../types/uploader';
import {
  getTransferBatchAssetType,
  getTransferBatchHasLocalData,
  getTransferBatchType,
  hasPartialFailure,
} from '../../utils';
import { ARROW_ICON_CLASSNAME } from '../JobCard';
import { JobMediaThumbnail } from '../JobCard/JobMediaThumbnail';
import { JobStatus } from '../JobCard/JobStatus';
import { getTrackColor } from '../JobCard/utils';
import TEXT from './generated/TEXT/UploaderSheetListItemContent';
import { UploadSheetMenuContent } from './UploaderSheetMenuContent';

TEXT.DEFINE(
  {
    /** Error message when all items in a batch transfer failed */
    allFailed: (batchType: string): string =>
      select(batchType, {
        DOWNLOAD: 'All downloads failed',
        RENDER: 'All exports failed',
        other: 'All uploads failed',
      }),
    /** Error message when a single item transfer failed */
    failed: (batchType: string): string =>
      select(batchType, {
        DOWNLOAD: 'Download failed',
        RENDER: 'Export failed',
        other: 'Upload failed',
      }),
    /** Title showing count of items being transferred */
    itemsCount: (count: number): string =>
      plural(count, `${count} Items`, '1 Item'),
    /** Title showing count of folders being transferred */
    foldersCount: (count: number): string =>
      plural(count, `${count} Folders`, '1 Folder'),
    /** Tooltip text for showing failed items */
    failedTooltip: 'Show failed items',
  },
  { plural, select }
);

const getJobTitle = (
  type: TransferJobType,
  name: string,
  topLevelFolderCount?: number | null,
  topLevelFileCount?: number | null
): string => {
  // Single Root File or Folder
  if (type !== 'BATCH') return name;

  const folderCount = topLevelFolderCount ?? 0;
  const fileCount = topLevelFileCount ?? 0;

  // Root Files + Folders
  if (folderCount > 0 && fileCount > 0) {
    return TEXT.itemsCount(fileCount + folderCount);
  }

  // Only Root folders
  if (folderCount > 0) {
    return TEXT.foldersCount(folderCount);
  }

  // Only Root Files
  return TEXT.itemsCount(fileCount);
};

type UploaderSheetListItemContentProps = {
  job: TransferBatch;
  pathToFile: string | undefined;
};

export const UploaderSheetListItemContent = memo(
  function UploaderSheetListItemContent(
    props: UploaderSheetListItemContentProps
  ) {
    const { job, pathToFile } = props;

    const {
      fileCounts,
      batchType,
      name,
      progress,
      status,
      topLevelFolderCount = 0,
      topLevelFileCount,
      targetFolder,
      paginatedAssetItems,
    } = job;

    // The type of the main asset in the transfer batch if not unsupported
    const transferBatchAssetType = getTransferBatchAssetType(
      paginatedAssetItems,
      name
    );

    const hasLocalData = getTransferBatchHasLocalData(job);
    const folderId = targetFolder?.id;
    const folderName = targetFolder?.name;
    const projectName = targetFolder?.project?.name;
    const isAtRoot = targetFolder?.parent === null;
    const jobType = getTransferBatchType(job);
    const jobFileExtension = name?.split('/').pop()?.split('-').pop();
    const jobMimeType = getMimeTypeFromFileExtension(jobFileExtension);

    const isTransfersComplete =
      status !== TransferBatchStatus.IDLE &&
      status !== TransferBatchStatus.PROCESSING &&
      status !== TransferBatchStatus.UPLOADING &&
      status !== TransferBatchStatus.DOWNLOADING;
    const partialFailed = hasPartialFailure(job);
    const hasFailed = status === TransferBatchStatus.FAILED && !partialFailed;
    const isCancelled = status === TransferBatchStatus.CANCELED;
    const isJobCanceling = status === TransferBatchStatus.CANCELING;

    const isOnlyFolders =
      topLevelFolderCount !== null &&
      topLevelFolderCount > 0 &&
      topLevelFileCount === 0;

    const iconType: TransferJobType = isOnlyFolders ? 'FOLDER' : jobType;

    /**
     * NOTE: REPLACE THIS LOGIC.
     * This is a temporary derivation for downloaded file extensions, based on
     * mimeType. Eventually this should be replaced with something more robust.
     */

    const fileExtension =
      jobFileExtension || jobMimeType?.split('/').pop()?.split('-').pop();

    const mimeType = getMimeTypeFromFileExtension(fileExtension);

    const showLinearProgress =
      !isTransfersComplete && hasLocalData && !isJobCanceling;

    const jobTitle = getJobTitle(
      jobType,
      name,
      topLevelFolderCount,
      topLevelFileCount
    );

    return (
      <ListItemContent
        adornmentBefore={
          <JobMediaThumbnail
            job={job}
            mimeType={mimeType}
            status={status}
            transferBatchAssetType={transferBatchAssetType}
            type={iconType}
          />
        }
        data-testid="uploader-sheet-cell"
        label={
          <AnimatedHeight
            initial={false}
            isShowing={true}
            transitionDuration={theme.transitions.durations[300]}
          >
            <Container>
              <TopMetaWithMenu>
                <LabelWithChip
                  $isCancelled={isCancelled}
                  data-testid={
                    isCancelled
                      ? 'job-card--is-cancelled'
                      : 'job-card--is-not-cancelled'
                  }
                >
                  <ListItemLabel enableTooltip={true} truncate={true}>
                    {jobTitle}
                  </ListItemLabel>
                  {partialFailed && (
                    <Chip
                      color="red"
                      iconBefore={<IconWarningFill />}
                      size="small"
                      style={{ flexShrink: 0 }}
                    >
                      {fileCounts?.failed}
                    </Chip>
                  )}
                </LabelWithChip>
                {folderId && (
                  <Location
                    $isCancelled={isCancelled}
                    data-testid="job-card--upload-location"
                  >
                    <CrustcrumbsView
                      folderName={folderName}
                      isAtRoot={isAtRoot}
                      projectName={projectName}
                    />
                  </Location>
                )}
                <MenuButtonContainer>
                  <ListItemContextMenu
                    content={
                      <UploadSheetMenuContent
                        job={job}
                        pathToFile={pathToFile}
                      />
                    }
                    variant="persist-button"
                  />
                </MenuButtonContainer>
              </TopMetaWithMenu>
              {/* When all files have failed */}
              {hasFailed && (
                <Box display="flex" style={{ gap: '4px' }}>
                  <IconWarningFill
                    color={theme.colors.icon.interactive.error}
                    data-testid="failed-icon"
                    size={16}
                  />
                  <Text
                    color="error"
                    data-testid="failed-text"
                    variant="secondary"
                  >
                    {jobType !== 'BATCH'
                      ? TEXT.failed(batchType)
                      : TEXT.allFailed(batchType)}
                  </Text>
                </Box>
              )}
              {showLinearProgress && (
                <LinearProgress
                  aria-label={`${name} upload progress`}
                  className="progress-bar"
                  value={progress?.percentage || 0}
                  variant={getTrackColor(job)}
                />
              )}
              <Text
                className="status"
                color="tertiary"
                variant="secondary"
                variantNumeric="tabular-nums"
              >
                <JobStatus
                  hasPartialFailure={partialFailed}
                  job={job}
                  status={status}
                />
              </Text>
            </Container>
          </AnimatedHeight>
        }
      />
    );
  }
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spaces[4]};

  .progress-bar {
    margin-top: ${theme.spaces[4]};
    margin-bottom: ${theme.spaces[4]};
  }

  .${ARROW_ICON_CLASSNAME} {
    /* Optically center arrow icon beneath job name */
    margin-left: -3px;
    margin-right: ${theme.spaces[2]};
    margin-top: -2px;
  }

  .status {
    align-items: center;
    display: flex;
    white-space: nowrap;
  }

  .checkmark {
    margin-right: ${theme.spaces[4]};
    vertical-align: middle;
  }
`;

const TopMetaWithMenu = styled.div`
  display: grid;
  grid-column-gap: ${theme.spaces[16]};
  grid-row-gap: ${theme.spaces[0]};
  grid-template-areas:
    'label-with-chip menu-button-container'
    'location menu-button-container';
  grid-template-columns: 1fr auto;
  width: 100%;
`;

const MenuButtonContainer = styled.div`
  align-items: center;
  display: flex;
  grid-area: menu-button-container;
`;

const LabelWithChip = styled.div<{ $isCancelled?: boolean }>`
  display: flex;
  gap: ${theme.spaces[4]};
  grid-area: label-with-chip;
  justify-content: space-between;
  max-width: 100%;
  min-width: 0;
  text-decoration: ${(p) => p.$isCancelled && 'line-through'};
  text-decoration-color: ${theme.colors.text.secondary};
`;

const Location = styled.div<{ $isCancelled?: boolean }>`
  grid-area: location;
  overflow: hidden;
  position: relative;
  text-decoration: ${(p) => p.$isCancelled && 'line-through'};
  text-decoration-color: ${theme.colors.text.secondary};
`;
